import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { Match, Round } from '../../api/types';
import { flagOf, matchHasBrasil } from '../../lib/teams';
import ScoreInput from '../../components/ScoreInput';

export default function AdminResultsPage() {
  const [roundId, setRoundId] = useState<number>(1);

  const { data: rounds } = useQuery({
    queryKey: ['rounds'],
    queryFn: async () => (await api.get<Round[]>('/rounds')).data,
  });

  const { data: matches } = useQuery({
    queryKey: ['matches', roundId],
    queryFn: async () => (await api.get<Match[]>(`/rounds/${roundId}/matches`)).data,
  });

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Resultados</h2>

      <select
        className="input w-40"
        value={roundId}
        onChange={(e) => setRoundId(parseInt(e.target.value))}
      >
        {rounds?.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </select>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b">
              <th className="py-2">Data</th>
              <th>Jogo</th>
              <th>Placar</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {matches?.map((m) => (
              <MatchRow key={m.id} match={m} roundId={roundId} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MatchRow({ match, roundId }: { match: Match; roundId: number }) {
  const qc = useQueryClient();
  const [sh, setSh] = useState<number | ''>(match.score_home ?? '');
  const [sa, setSa] = useState<number | ''>(match.score_away ?? '');
  const [status, setStatus] = useState<string>(match.status);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const brHere = matchHasBrasil(match.team_home, match.team_away);

  const save = useMutation({
    mutationFn: async () =>
      (
        await api.post<Match>(`/admin/matches/${match.id}/result`, {
          score_home: typeof sh === 'number' ? sh : 0,
          score_away: typeof sa === 'number' ? sa : 0,
          status,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['matches', roundId] });
      setFeedback({ kind: 'ok', text: '✓ Salvo' });
      setTimeout(() => setFeedback(null), 2500);
    },
    onError: (err: any) => {
      setFeedback({
        kind: 'err',
        text: err.response?.data?.message || 'Erro ao salvar',
      });
    },
  });

  return (
    <tr className={`border-b ${brHere ? 'bg-yellow-50' : ''}`}>
      <td className="py-2 whitespace-nowrap">
        {new Date(match.match_date).toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </td>
      <td className={brHere ? 'font-bold' : ''}>
        <span className="mr-1">{flagOf(match.team_home)}</span>
        {match.team_home}
        <span className="mx-1">×</span>
        <span className="mr-1">{flagOf(match.team_away)}</span>
        {match.team_away}
      </td>
      <td>
        <div className="flex gap-1 items-center">
          <ScoreInput value={sh} onChange={(v) => setSh(v)} ariaLabel="Placar casa" />
          <span>×</span>
          <ScoreInput value={sa} onChange={(v) => setSa(v)} ariaLabel="Placar fora" />
        </div>
      </td>
      <td>
        <select className="input py-1" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="scheduled">Agendado</option>
          <option value="live">Ao vivo</option>
          <option value="finished">Finalizado</option>
        </select>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <button
            className="btn btn-primary text-xs py-1"
            disabled={sh === '' || sa === '' || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </button>
          {feedback && (
            <span
              className={`text-xs ${
                feedback.kind === 'ok' ? 'text-brand-600' : 'text-red-600'
              }`}
            >
              {feedback.text}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}
